import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SignificanceHeader, SignificanceCell } from './SignificanceGuideHint';
import { buildVariantGridRows } from '../../utils/variantGridUtils';
import { BRAND } from '../../theme/brandTheme';

const positiveSx = { color: BRAND.green, fontWeight: 600 };
const negativeSx = { color: BRAND.redLink, fontWeight: 600 };

// DataGrid v6 passes { value, ... } to valueFormatter, not the raw cell value
const numValue = (params) => Number(params?.value ?? 0) || 0;

/** Variant results table via MUI DataGrid */
const ReportVariantGrid = ({ originalData, analysis, sessionTab, conversionTab }) => {
  const rows = useMemo(
    () => buildVariantGridRows(originalData, analysis),
    [originalData, analysis],
  );

  const columns = useMemo(
    () => [
      {
        field: 'variant',
        headerName: 'Variant',
        flex: 1.2,
        minWidth: 120,
      },
      {
        field: 'sessions',
        headerName: sessionTab || 'Sessions',
        flex: 1,
        minWidth: 100,
        type: 'number',
        valueFormatter: (params) => numValue(params).toLocaleString(),
      },
      {
        field: 'conversions',
        headerName: conversionTab || 'Transactions',
        flex: 1,
        minWidth: 100,
        type: 'number',
        valueFormatter: (params) => numValue(params).toLocaleString(),
      },
      {
        field: 'cr',
        headerName: 'Conv. Rate',
        flex: 0.9,
        minWidth: 95,
        valueFormatter: (params) => `${numValue(params).toFixed(2)}%`,
      },
      {
        field: 'uplift',
        headerName: 'Uplift',
        flex: 0.8,
        minWidth: 80,
        renderCell: ({ row, value }) => {
          if (row.isControl) return '-';
          const num = Number(value) || 0;
          return (
            <Box component="span" sx={num >= 0 ? positiveSx : negativeSx}>
              {num.toFixed(2)}%
            </Box>
          );
        },
      },
      {
        field: 'significance',
        headerName: 'Signif.',
        flex: 0.9,
        minWidth: 90,
        renderHeader: () => <SignificanceHeader />,
        renderCell: ({ value }) => (
          <SignificanceCell value={numValue({ value }).toFixed(1)} />
        ),
      },
      {
        field: 'monthly',
        headerName: 'Monthly',
        flex: 0.9,
        minWidth: 90,
        headerClassName: 'report-export-hide',
        cellClassName: 'report-export-hide',
        renderCell: ({ row, value }) => {
          if (row.isControl) return '-';
          const num = Number(value) || 0;
          return (
            <Box component="span" sx={num >= 0 ? positiveSx : negativeSx}>
              {Math.round(num).toLocaleString()}
            </Box>
          );
        },
      },
      {
        field: 'yearly',
        headerName: 'Yearly',
        flex: 0.9,
        minWidth: 90,
        headerClassName: 'report-export-hide',
        cellClassName: 'report-export-hide',
        renderCell: ({ row, value }) => {
          if (row.isControl) return '-';
          const num = Number(value) || 0;
          return (
            <Box component="span" sx={num >= 0 ? positiveSx : negativeSx}>
              {Math.round(num).toLocaleString()}
            </Box>
          );
        },
      },
    ],
    [sessionTab, conversionTab],
  );

  return (
    <Box
      className="test-results"
      sx={{
        width: '100%',
        '& .MuiDataGrid-root': { border: `1px solid ${BRAND.borderLight}`, borderRadius: 1 },
        '& .MuiDataGrid-columnHeaders': { bgcolor: '#f1f1f1', fontWeight: 600 },
        '& .control-row': { bgcolor: '#fcfcfc' },
        '& .significance-cell': { cursor: 'help', borderBottom: '1px dotted #999' },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        getRowClassName={({ row }) => (row.isControl ? 'control-row' : 'variant-row')}
        sx={{
          fontSize: 14,
          '& .MuiDataGrid-cell': { outline: 'none' },
        }}
      />
    </Box>
  );
};

export default ReportVariantGrid;
